import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { api } from "@/lib/api";
import { Moon, Sun, LogOut, LayoutDashboard, Globe } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (p) => (p && !/^https?:/i.test(p) ? `${BACKEND_URL}${p}` : p);

export default function Header({ siteTitle = "NetKom Portal", siteLogo: siteLogoProp = null, siteLogoDark: siteLogoDarkProp = null, centerSlot = null }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const [fetched, setFetched] = React.useState({ light: "", dark: "" });

  // Récupère le logo uniquement si non fourni par le parent
  React.useEffect(() => {
    if (siteLogoProp !== null) return;
    let cancelled = false;
    api
      .get("/public/site")
      .then(({ data }) => {
        if (!cancelled) setFetched({ light: data.site_logo_path || "", dark: data.site_logo_dark_path || "" });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [siteLogoProp]);

  const siteLogo = siteLogoProp !== null ? (siteLogoProp || "") : fetched.light;
  const siteLogoDark = siteLogoDarkProp !== null ? (siteLogoDarkProp || "") : fetched.dark;

  // Choisit le bon logo selon le thème, avec fallback
  const displayedLogo = theme === "dark" && siteLogoDark ? siteLogoDark : siteLogo;
  const useBlendMode = !(theme === "dark" && siteLogoDark); // pas de blend si logo dédié dark

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="header-logo-link">
          {displayedLogo ? (
            <img
              src={absUrl(displayedLogo)}
              alt={siteTitle}
              className="h-14 w-auto max-w-[280px] object-contain"
              style={useBlendMode ? { mixBlendMode: theme === "dark" ? "screen" : "multiply", background: "transparent" } : { background: "transparent" }}
              data-testid="header-site-logo"
            />
          ) : (
            <>
              <div className="h-9 w-9 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-black text-lg tracking-tighter">
                N
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-black tracking-tight text-sm sm:text-base" data-testid="header-site-title">
                  {siteTitle}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  Plateforme interne
                </span>
              </div>
            </>
          )}
        </Link>

        {/* Slot central pour annonces / contenu personnalisé */}
        {centerSlot && (
          <div className="flex-1 flex justify-center min-w-0 px-2" data-testid="header-center-slot">
            {centerSlot}
          </div>
        )}

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={toggle}
            className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-border bg-background hover:bg-accent transition-colors"
            aria-label="Basculer le thème"
            data-testid="theme-toggle-button"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {user && user.role === "admin" ? (
            <>
              {!isAdmin ? (
                <Link
                  to="/admin"
                  className="h-9 px-3 inline-flex items-center gap-2 rounded-sm border border-border bg-background hover:bg-accent text-sm font-medium transition-colors"
                  data-testid="header-admin-link"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span className="hidden sm:inline">Backoffice</span>
                </Link>
              ) : (
                <Link
                  to="/"
                  className="h-9 px-3 inline-flex items-center gap-2 rounded-sm border border-border bg-background hover:bg-accent text-sm font-medium transition-colors"
                  data-testid="header-public-link"
                >
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">Vue publique</span>
                </Link>
              )}
              <button
                onClick={logout}
                className="h-9 px-3 inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors"
                data-testid="header-logout-button"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </>
          ) : (
            <Link
              to="/admin/login"
              className="h-9 px-3 inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors"
              data-testid="header-login-link"
            >
              Admin
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
